import type { TFunction } from 'i18next';
import {
  getCountryConfig,
  getRegistrationTypeConfig,
  type RegistrationTypeCode,
  type SupportedCountryCode,
} from '../config/countryConfig';

export type RegistrationErrorCode =
  | 'REQUIRED'
  | 'SIRET_LENGTH'
  | 'BCE_LENGTH'
  | 'BCE_PREFIX'
  | 'BCE_CHECKSUM'
  | 'KVK_LENGTH'
  | 'TYPE_INVALID';

const ERROR_CODE_TO_I18N: Record<RegistrationErrorCode, string> = {
  REQUIRED: 'errors.required',
  SIRET_LENGTH: 'errors.siretLength',
  BCE_LENGTH: 'errors.bceLength',
  BCE_PREFIX: 'errors.bcePrefix',
  BCE_CHECKSUM: 'errors.bceChecksum',
  KVK_LENGTH: 'errors.kvkLength',
  TYPE_INVALID: 'errors.typeInvalid',
};

export function translateRegistrationError(
  tReg: TFunction,
  errorCode?: RegistrationErrorCode | string | null
): string {
  if (!errorCode) return tReg('errors.required');
  const key = ERROR_CODE_TO_I18N[errorCode as RegistrationErrorCode];
  return key ? tReg(key) : tReg('errors.invalid');
}

function digitsOnly(value: string): string {
  return String(value || '').replace(/\D/g, '');
}

export function validateSiret(value: string): {
  ok: boolean;
  errorCode?: RegistrationErrorCode;
  normalized?: string;
} {
  const digits = digitsOnly(value);
  if (digits.length !== 14) {
    return { ok: false, errorCode: 'SIRET_LENGTH' };
  }
  return { ok: true, normalized: digits };
}

export function validateBce(value: string): {
  ok: boolean;
  errorCode?: RegistrationErrorCode;
  normalized?: string;
} {
  const digits = digitsOnly(value);
  if (digits.length !== 10) {
    return { ok: false, errorCode: 'BCE_LENGTH' };
  }
  if (digits[0] !== '0' && digits[0] !== '1') {
    return { ok: false, errorCode: 'BCE_PREFIX' };
  }
  const base = parseInt(digits.slice(0, 8), 10);
  const check = parseInt(digits.slice(8), 10);
  let expected = 97 - (base % 97);
  if (expected === 0) expected = 97;
  if (check !== expected) {
    return { ok: false, errorCode: 'BCE_CHECKSUM' };
  }
  return { ok: true, normalized: digits };
}

export function validateKvk(value: string): {
  ok: boolean;
  errorCode?: RegistrationErrorCode;
  normalized?: string;
} {
  const digits = digitsOnly(value);
  if (digits.length !== 8) {
    return { ok: false, errorCode: 'KVK_LENGTH' };
  }
  return { ok: true, normalized: digits };
}

const VALIDATORS: Record<
  RegistrationTypeCode,
  (value: string) => {
    ok: boolean;
    errorCode?: RegistrationErrorCode;
    normalized?: string;
  }
> = {
  siret: validateSiret,
  bce: validateBce,
  kvk: validateKvk,
};

export function validateRegistrationId(
  country: string,
  registrationType: string | undefined,
  registrationId: string
): {
  ok: boolean;
  errorCode?: RegistrationErrorCode;
  normalized?: string;
  type: RegistrationTypeCode;
} {
  const cfg = getCountryConfig(country);
  const type = (registrationType || cfg.defaultRegistrationType) as RegistrationTypeCode;
  const allowed = new Set(cfg.registrationTypes.map((rt) => rt.type));
  if (!allowed.has(type)) {
    return { ok: false, errorCode: 'TYPE_INVALID', type };
  }
  if (!registrationId?.trim()) {
    return { ok: false, errorCode: 'REQUIRED', type };
  }
  const result = VALIDATORS[type](registrationId);
  return { ...result, type };
}

/** Payload API unifié pour create/update-pro. */
export function buildRegistrationPayload(
  country: SupportedCountryCode | string,
  registrationId: string,
  registrationType?: string
): {
  country: SupportedCountryCode;
  business_registration_type: RegistrationTypeCode;
  business_registration_id: string;
  siret?: string;
} {
  const cc = getCountryConfig(country).code;
  const validated = validateRegistrationId(cc, registrationType, registrationId);
  if (!validated.ok || !validated.normalized) {
    const err = new Error(validated.errorCode || 'REQUIRED') as Error & {
      errorCode: RegistrationErrorCode;
    };
    err.errorCode = validated.errorCode || 'REQUIRED';
    throw err;
  }
  const type = validated.type;
  const payload: {
    country: SupportedCountryCode;
    business_registration_type: RegistrationTypeCode;
    business_registration_id: string;
    siret?: string;
  } = {
    country: cc,
    business_registration_type: type,
    business_registration_id: validated.normalized,
  };
  if (cc === 'FR' && type === 'siret') {
    payload.siret = validated.normalized;
  }
  return payload;
}

export function hasRegistrationId(pro?: {
  siret?: string | null;
  business_registration_id?: string | null;
} | null): boolean {
  return Boolean(
    (pro?.business_registration_id && String(pro.business_registration_id).trim()) ||
      (pro?.siret && String(pro.siret).trim())
  );
}

/** Longueur max du champ (valeur formatée avec espaces / points). */
export function getRegistrationIdInputMaxLength(
  country: string,
  registrationType?: string
): number {
  const { digitLength } = getRegistrationTypeConfig(country, registrationType);
  return formatRegistrationIdDisplay(
    country,
    registrationType,
    '0'.repeat(digitLength)
  ).length;
}

/** Saisie / collage : chiffres seuls, tronqués à digitLength, puis formatés. */
export function applyRegistrationIdInput(
  country: string,
  registrationType: string | undefined,
  rawValue: string
): string {
  return formatRegistrationIdDisplay(country, registrationType, rawValue);
}

/** Affichage saisie : KvK `1234 5678`, BCE `0123.456.749`, SIRET groupes de 3. */
export function formatRegistrationIdDisplay(
  country: string,
  registrationType: string | undefined,
  value: string
): string {
  const digits = digitsOnly(value);
  const cfg = getCountryConfig(country);
  const type = (registrationType || cfg.defaultRegistrationType) as RegistrationTypeCode;
  if (type === 'kvk') {
    const d = digits.slice(0, 8);
    if (d.length <= 4) return d;
    return `${d.slice(0, 4)} ${d.slice(4)}`;
  }
  if (type === 'bce') {
    const d = digits.slice(0, 10);
    if (d.length <= 4) return d;
    if (d.length <= 7) return `${d.slice(0, 4)}.${d.slice(4)}`;
    return `${d.slice(0, 4)}.${d.slice(4, 7)}.${d.slice(7)}`;
  }
  // SIRET — 14 chiffres, groupes de 3
  const d = digits.slice(0, 14);
  return d.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
}
